import { Injectable, Injector } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { LoggerService } from '@core/services/logger/logger.service';
import { AppSettingsService } from '@core/services/app-settings/app-settings.service';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { delayedRetry } from '@core/operators/delayedRetry';
import { HttpUtils } from '@shared/utils/http-utils';

/** 401 / 403 responses handled by AuthInterceptor (token refresh or redirect to login)
 * should not produce per-call error logs — they're expected auth transitions.
 * If these statuses flood in repeatedly it signals a real problem, so we log
 * a single aggregated error once the rate exceeds THROTTLE_THRESHOLD within THROTTLE_FLOOD_WINDOW_MS.
 */
const THROTTLED_STATUSES = new Set([401, 403]);
const THROTTLE_THRESHOLD = 10;
const THROTTLE_FLOOD_WINDOW_MS = 60_000;

const SKIP_RETRY_STATUSES = new Set([400, 401, 409]);

interface ThrottledEvent {
	timestamp: number;
	status: number;
	url: string;
	message: string;
	exceptionMessage: string;
}

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
	public readonly name = 'HttpErrorInterceptor';

	private throttledEvents: ThrottledEvent[] = [];

	constructor(private injector: Injector) {}

	intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
		const retryIntervals = this.injector.get(AppSettingsService)?.retryIntervals ?? [];
		const staticFilesEndpoint = this.injector.get(ShellQuery)?.staticFilesEndpoint;

		if (request.url.startsWith(staticFilesEndpoint)) {
			return next.handle(request.clone()).pipe(catchError(err => this.logError(err)));
		}

		return next.handle(request.clone()).pipe(
			delayedRetry(retryIntervals, (error: Error) => {
				return error instanceof HttpErrorResponse && SKIP_RETRY_STATUSES.has(error.status);
			}),
			catchError(err => this.logError(err))
		);
	}

	/**
	 * Updates the throttled-event window.
	 * Returns flood metadata when the threshold is exceeded,
	 * or `null` when the event count is still within the allowed threshold (caller should suppress logging).
	 */
	private handleThrottledError(error: HttpErrorResponse): { message: string; parameters: { [key: string]: string } } | null {
		const now = Date.now();

		const currentEvents = [
			...this.throttledEvents.filter(e => e.timestamp >= now - THROTTLE_FLOOD_WINDOW_MS),
			{
				timestamp: now,
				status: error.status,
				url: error.url ?? '',
				message: error.message,
				exceptionMessage: error.error?.ExceptionMessage ?? ''
			}
		];

		if (currentEvents.length <= THROTTLE_THRESHOLD) {
			this.throttledEvents = currentEvents;

			return null;
		}

		this.throttledEvents = [];

		const uniqueCodes = [...new Set(currentEvents.map(e => e.status))].join(', ');

		return {
			message: `Error flood detected: ${currentEvents.length} HTTP ${uniqueCodes} responses`,
			parameters: {
				allErrorMessages: currentEvents.map(e => e.message).join(' | '),
				allExceptionMessages: currentEvents
					.map(e => e.exceptionMessage)
					.filter(Boolean)
					.join(' | '),
				allCodes: uniqueCodes,
				allUrls: currentEvents.map(e => e.url).join(' | ')
			}
		};
	}

	private logError(error: HttpErrorResponse) {
		let message = error.message;
		const logger = this.injector.get(LoggerService);
		let extendedParameters: { [key: string]: string } = {
			url: error?.url ?? '',
			statusCode: error?.status.toString()
		};

		const traceId = HttpUtils.getTraceId(error);

		if (traceId) {
			extendedParameters = { ...extendedParameters, traceId };
		}

		if (THROTTLED_STATUSES.has(error.status)) {
			const floodResult = this.handleThrottledError(error);

			if (floodResult === null) {
				return throwError(error);
			}

			message = floodResult.message;
			extendedParameters = { ...extendedParameters, ...floodResult.parameters };
		}

		if (error.error?.ExceptionMessage) {
			extendedParameters = { ...extendedParameters, exceptionMessage: error.error?.ExceptionMessage };
		}

		logger.error(message, {
			module: this.name,
			extendedParameters
		});

		return throwError(error);
	}
}
